"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.default = Notes;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const react_native_1 = require("react-native");
const react_redux_1 = require("react-redux");
const pro_light_svg_icons_1 = require("@fortawesome/pro-light-svg-icons");
const user_1 = require("../../../store/selectors/user");
const useDevice_1 = require("../../../hooks/useDevice");
const useRequest_1 = __importDefault(require("../../../hooks/useRequest"));
const useDocument_1 = __importDefault(require("../../../hooks/useDocument"));
const ScrollCustomIndicator_1 = __importDefault(require("../../../component/ScrollCustomIndicator"));
const AppLoader_1 = __importDefault(require("../../../component/AppLoader"));
const AppText_1 = require("../../../component/AppText");
const NoteItem_1 = __importDefault(require("./NoteItem"));
const AddNoteButton_1 = __importDefault(require("./AddNoteButton"));
const NotesMenuButton_1 = __importDefault(require("./NotesMenuButton"));
const CloseNotesButton_1 = __importDefault(require("./CloseNotesButton"));
const AcceptButton_1 = __importDefault(require("./AcceptButton"));
const ActionButton_1 = __importDefault(require("./ActionButton"));
const NotesHeaderIcon_1 = __importDefault(require("./NotesHeaderIcon"));
const document_1 = require("../../../services/document");
const colors_1 = require("../../../common/colors");
const COMPACT_SIZE = 400;
const ACTIONS = [
    { name: 'FAQs', icon: pro_light_svg_icons_1.faQuestionCircle, action: 'faqs' },
    { name: 'Project Brief', icon: pro_light_svg_icons_1.faClipboard, action: 'brief' },
    { name: 'Case Study', icon: pro_light_svg_icons_1.faBookmark, action: 'case-study' },
    { name: 'TOC', icon: pro_light_svg_icons_1.faList, action: 'toc' },
];
function Notes(props) {
    const { isVisible, onClose } = props;
    const { isMobileScreen } = (0, useDevice_1.useDevice)();
    const { request } = (0, useRequest_1.default)();
    const { followDocument } = (0, useDocument_1.default)();
    const user = (0, react_redux_1.useSelector)(user_1.selectUser);
    const [notes, setNotes] = (0, react_1.useState)([]);
    const [selectedNoteIndex, setSelectedNoteIndex] = (0, react_1.useState)(0);
    const [noteTitle, setNoteTitle] = (0, react_1.useState)('');
    const [noteContent, setNoteContent] = (0, react_1.useState)('');
    const [followedCharacterId, setFollowedCharacterId] = (0, react_1.useState)();
    const [followedConversationId, setFollowedConversationId] = (0, react_1.useState)();
    const [isMenuActive, setIsMenuActive] = (0, react_1.useState)(false);
    const [isEditing, setIsEditing] = (0, react_1.useState)(false);
    const [isLoading, setIsLoading] = (0, react_1.useState)(false);
    const [isCompactSize, setIsCompactSize] = (0, react_1.useState)(false);
    const wasRequestedControl = (0, react_1.useRef)(false);
    const wasUnmountedRef = (0, react_1.useRef)(false);
    const noteInputRef = (0, react_1.useRef)(null);
    const fakeInputRef = (0, react_1.useRef)(null);
    const characterId = (0, react_1.useMemo)(() => ('characterId' in props ? props.characterId : followedCharacterId), [props, followedCharacterId]);
    const conversationId = (0, react_1.useMemo)(() => ('conversationId' in props ? props.conversationId : followedConversationId), [props, followedConversationId]);
    const blurInput = (0, react_1.useCallback)(() => {
        fakeInputRef.current?.focus();
    }, []);
    const createNewNote = (0, react_1.useCallback)(() => {
        if (characterId) {
            setIsLoading(true);
            request(document_1.createNote, '', `Untitled Note ${notes.length + 1}`, characterId)
                .then((newNote) => {
                setIsLoading(false);
                if (newNote) {
                    setNotes((previousNotes) => [newNote, ...previousNotes]);
                    setNoteTitle(newNote.title);
                    setNoteContent(newNote.note);
                    setSelectedNoteIndex(0);
                }
                ;
            });
        }
    }, [notes, characterId, request]);
    const updateCurrentNote = (0, react_1.useCallback)(() => {
        setIsLoading(true);
        request(document_1.updateNote, notes[selectedNoteIndex].id, noteContent, noteTitle)
            .then((updatedNote) => {
            setIsLoading(false);
            if (updatedNote) {
                const updatedIndex = notes.findIndex((note) => note.id === updatedNote.id);
                setIsEditing(false);
                blurInput();
                setNotes((previousNotes) => [
                    updatedNote,
                    ...previousNotes.slice(0, updatedIndex),
                    ...previousNotes.slice(updatedIndex + 1),
                ]);
                setSelectedNoteIndex(0);
            }
        });
    }, [notes, selectedNoteIndex, noteTitle, noteContent, blurInput]);
    const cancelEditing = (0, react_1.useCallback)(() => {
        setNoteTitle(notes[selectedNoteIndex].title);
        setNoteContent(notes[selectedNoteIndex].note);
        setIsEditing(false);
        blurInput();
    }, [notes, selectedNoteIndex, blurInput]);
    (0, react_1.useEffect)(() => {
        return () => { wasUnmountedRef.current = true; };
    }, []);
    (0, react_1.useEffect)(() => {
        const fetchNotes = (characterId) => {
            request(document_1.getNotes, characterId).then((response) => {
                if (response && !wasUnmountedRef.current) {
                    if (response.length > 0) {
                        setIsLoading(false);
                        setNotes(response);
                        setNoteTitle(response[0].title);
                        setNoteContent(response[0].note);
                    }
                    else {
                        request(document_1.createNote, '', 'Untitled Note', characterId).then((note) => {
                            setIsLoading(false);
                            if (note) {
                                setNotes([note]);
                                setNoteTitle(note.title);
                                setNoteContent(note.note);
                            }
                            ;
                        });
                    }
                    setSelectedNoteIndex(0);
                    noteInputRef.current?.focus();
                }
                ;
            });
        };
        let isUnmounted = false;
        if (!wasRequestedControl.current && user) {
            setIsLoading(true);
            if ('conversationId' in props) {
                wasRequestedControl.current = true;
                fetchNotes(props.characterId);
            }
            else if (isVisible) {
                wasRequestedControl.current = true;
                request(followDocument, props.documentShareId).then((document) => {
                    if (document) {
                        fetchNotes(document.character.id.toString());
                        setFollowedCharacterId(document.character.id.toString());
                        setFollowedConversationId(document.conversation.id);
                    }
                    ;
                });
            }
            ;
        }
        ;
    }, [user, isVisible, props, request]);
    (0, react_1.useEffect)(() => {
        if (isVisible) {
            noteInputRef.current?.focus();
        }
    }, [isVisible]);
    const handleContainerLayout = (0, react_1.useCallback)(({ nativeEvent: { layout: { width } } }) => (setIsCompactSize(width < COMPACT_SIZE)), []);
    const handleMenuPress = (0, react_1.useCallback)(() => {
        setIsMenuActive(true);
        setIsEditing(false);
    }, []);
    const handleNoteSelection = (0, react_1.useCallback)((index) => {
        setSelectedNoteIndex(index);
        setNoteTitle(notes[index].title);
        setNoteContent(notes[index].note);
        setIsMenuActive(false);
        setTimeout(() => {
            noteInputRef.current?.focus();
        });
    }, [notes]);
    const handleDeleteNoteStart = (0, react_1.useCallback)(() => {
        setIsLoading(true);
    }, []);
    const handleDeleleNote = (0, react_1.useCallback)(() => {
        setIsLoading(false);
    }, []);
    const handleDeleteNoteSuccessufully = (0, react_1.useCallback)((note) => {
        setNotes((previousNote) => previousNote.filter((previousNote) => previousNote.id !== note.id));
        setSelectedNoteIndex(-1);
    }, []);
    const handleActionPress = (0, react_1.useCallback)((action, actionName) => {
        const currentNote = notes[selectedNoteIndex];
        if (currentNote[action]) {
            setNoteContent(currentNote[action]);
            setNoteTitle(actionName);
            noteInputRef.current?.focus();
        }
        else {
            setIsLoading(true);
            characterId
                && conversationId
                && request(document_1.createTemplate, currentNote.id, action, characterId, conversationId)
                    .then((templateResponse) => {
                    setIsLoading(false);
                    if (templateResponse) {
                        setNotes((previousNotes) => ([
                            ...previousNotes.slice(0, selectedNoteIndex),
                            { ...currentNote, [action]: templateResponse },
                            ...previousNotes.slice(selectedNoteIndex + 1),
                        ]));
                        setNoteContent(templateResponse);
                        setNoteTitle(actionName);
                        setIsEditing(true);
                        noteInputRef.current?.focus();
                    }
                    ;
                });
        }
        ;
    }, [notes, selectedNoteIndex, characterId, conversationId]);
    const handleInputFocus = (0, react_1.useCallback)(() => {
        setIsEditing(true);
    }, []);
    const handleInputBlur = (0, react_1.useCallback)(() => {
        if (notes[selectedNoteIndex].title === noteTitle
            && notes[selectedNoteIndex].note === noteContent) {
            setIsEditing(false);
        }
    }, [notes, selectedNoteIndex, noteTitle, noteContent]);
    const handleTitleChange = (0, react_1.useCallback)((newTitle) => {
        setNoteTitle(newTitle);
    }, []);
    const handleNoteChange = (0, react_1.useCallback)((newNote) => {
        setNoteContent(newNote);
    }, []);
    if (!isVisible) {
        return (0, jsx_runtime_1.jsx)(jsx_runtime_1.Fragment, {});
    }
    ;
    return ((0, jsx_runtime_1.jsxs)(react_native_1.View, { onLayout: handleContainerLayout, children: [(isCompactSize || isMobileScreen) && ((0, jsx_runtime_1.jsxs)(react_native_1.View, { style: styles.mobileHeader, children: [(0, jsx_runtime_1.jsxs)(react_native_1.View, { style: styles.headerLeftSide, children: [isMenuActive ? ((0, jsx_runtime_1.jsx)(NotesHeaderIcon_1.default, {})) : ((0, jsx_runtime_1.jsx)(NotesMenuButton_1.default, { disabled: isLoading, onPress: handleMenuPress })), (0, jsx_runtime_1.jsx)(AppText_1.AppText, { style: [styles.headerTitle, isMenuActive && styles.headerMenuTitle], children: "Notes" })] }), !isEditing && ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsx)(AddNoteButton_1.default, { text: isMenuActive, disabled: isLoading, isCompactSize: isCompactSize, onPress: createNewNote }), (0, jsx_runtime_1.jsx)(CloseNotesButton_1.default, { onPress: onClose })] }))] })), (0, jsx_runtime_1.jsxs)(react_native_1.View, { style: [
                    styles.container,
                    (isCompactSize || isMobileScreen) && styles.containerMobile,
                    isMenuActive && styles.containerMenu,
                ], children: [isLoading && ((0, jsx_runtime_1.jsx)(AppLoader_1.default, { isLoading: true, showLoader: true })), (!isCompactSize && !isMobileScreen || isMenuActive) && ((0, jsx_runtime_1.jsxs)(react_native_1.View, { style: [
                            (isCompactSize || isMobileScreen) && styles.sideContainerMobile,
                            !isCompactSize && !isMobileScreen && styles.sideContainerDesktop,
                        ], children: [!isCompactSize && !isMobileScreen && ((0, jsx_runtime_1.jsxs)(react_native_1.View, { style: styles.sideTitleContainer, children: [(0, jsx_runtime_1.jsx)(AppText_1.AppText, { style: styles.sideTitle, children: "Notes" }), (0, jsx_runtime_1.jsx)(AddNoteButton_1.default, { onPress: createNewNote })] })), (0, jsx_runtime_1.jsx)(ScrollCustomIndicator_1.default, { customIndicator: !isMobileScreen, contentContainerStyle: styles.notesScroll, children: notes.map((note, index) => ((0, jsx_runtime_1.jsx)(NoteItem_1.default, { note: note, isActive: index === selectedNoteIndex, isCompactSize: isCompactSize, onPress: () => handleNoteSelection(index), onDeleteStart: handleDeleteNoteStart, onDelete: handleDeleleNote, onDeleteSuccessufully: handleDeleteNoteSuccessufully }, note.id))) })] })), !isMenuActive && ((0, jsx_runtime_1.jsxs)(react_native_1.View, { style: [
                            styles.noteContent,
                            !isCompactSize && !isMobileScreen && styles.noteContentDesktop,
                        ], children: [(0, jsx_runtime_1.jsxs)(react_native_1.View, { style: styles.noteTitle, children: [(0, jsx_runtime_1.jsx)(react_native_1.TextInput, { value: noteTitle, onChangeText: handleTitleChange, onFocus: handleInputFocus, onBlur: handleInputBlur, numberOfLines: 1, style: [
                                            styles.titleInput,
                                            (isCompactSize || isMobileScreen) && styles.titleInputMobile,
                                            { outline: 'none' }
                                        ] }), isEditing && ((0, jsx_runtime_1.jsxs)(react_native_1.View, { style: styles.titleButtons, children: [(0, jsx_runtime_1.jsx)(CloseNotesButton_1.default, { onPress: cancelEditing }), (0, jsx_runtime_1.jsx)(AcceptButton_1.default, { onPress: updateCurrentNote })] }))] }), (0, jsx_runtime_1.jsx)(react_native_1.TextInput, { ref: noteInputRef, value: noteContent, onChangeText: handleNoteChange, onFocus: handleInputFocus, onBlur: handleInputBlur, multiline: true, placeholder: 'Add a Note', placeholderTextColor: colors_1.Colors.PLACEHOLDER_DARK_COLOR, style: [styles.noteInput, { outline: 'none' }] }), noteContent?.length === 0 && ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsx)(AppText_1.AppText, { style: styles.generateText, children: "Or generate..." }), (0, jsx_runtime_1.jsx)(ScrollCustomIndicator_1.default, { customIndicator: !isMobileScreen, horizontal: true, containerStyle: styles.actionsScrollContainer, style: styles.actionsScroll, contentContainerStyle: styles.actionButtons, children: ACTIONS.map(({ name, icon, action }) => ((0, jsx_runtime_1.jsx)(ActionButton_1.default, { name: name, icon: icon, isCompactSize: isCompactSize, onPress: () => handleActionPress(action, name) }, name))) })] }))] }))] }), (0, jsx_runtime_1.jsx)(react_native_1.TextInput, { ref: fakeInputRef, style: styles.fakeInput })] }));
}
;
const styles = react_native_1.StyleSheet.create({
    container: {
        backgroundColor: colors_1.Colors.NEUTRAL_75,
        height: 248,
        gap: 16,
        flexDirection: 'row',
        borderRadius: 16,
    },
    containerMobile: {
        height: 263,
        padding: 16,
        paddingRight: 12,
        borderTopLeftRadius: 0,
        borderTopRightRadius: 0,
    },
    containerMenu: {
        height: 384,
        paddingRight: 16,
    },
    sideContainerDesktop: {
        width: 136,
        gap: 8,
        paddingVertical: 16,
        paddingLeft: 16,
    },
    sideContainerMobile: {
        flex: 1,
    },
    sideTitleContainer: {
        height: 24,
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    sideTitle: {
        color: colors_1.Colors.DARK_100,
        fontSize: 12,
        fontWeight: '700',
    },
    mobileHeader: {
        backgroundColor: '#2C2C31',
        gap: 16,
        flexDirection: 'row',
        flex: 1,
        paddingHorizontal: 16,
        paddingVertical: 12,
        borderBottomWidth: 2,
        borderColor: colors_1.Colors.NEUTRAL_100,
        borderTopLeftRadius: 24,
        borderTopRightRadius: 24,
    },
    headerLeftSide: {
        gap: 8,
        flexDirection: 'row',
        alignItems: 'center',
        flex: 1,
    },
    noteContent: {
        maxWidth: '100%',
        gap: 16,
        flex: 1,
    },
    noteContentDesktop: {
        backgroundColor: 'rgba(18, 18, 24, 0.30)',
        paddingVertical: 16,
        paddingBottom: 32,
        paddingLeft: 8,
        paddingRight: 12,
    },
    headerTitle: {
        color: colors_1.Colors.white,
        fontSize: 20,
        fontWeight: '700',
    },
    headerMenuTitle: {
        fontSize: 22,
        fontWeight: '400',
    },
    noteTitle: {
        flexDirection: 'row',
        justifyContent: 'space-between',
    },
    titleInput: {
        color: colors_1.Colors.white,
        fontSize: 15,
        fontWeight: '700',
        lineHeight: 24,
        width: '100%',
    },
    titleInputMobile: {
        fontSize: 18,
        lineHeight: 32,
    },
    titleButtons: {
        flexDirection: 'row',
        gap: 12,
    },
    noteInput: {
        color: colors_1.Colors.white,
        fontSize: 12,
        lineHeight: 20,
        flex: 1,
    },
    noteInputMobile: {
        color: '#FFF',
        fontSize: 16,
    },
    generateText: {
        color: colors_1.Colors.DARK_100,
        fontSize: 12,
        lineHeight: 20,
    },
    actionsScrollContainer: {
        flex: undefined,
    },
    actionsScroll: {
        flexGrow: 0,
    },
    actionButtons: {
        gap: 4,
    },
    fakeInput: {
        height: 0,
    },
    notesScroll: {
        gap: 8,
    },
});
